import type {
  TranslationOverviewCatalogListParams,
  TranslationOverviewCatalogLocaleRow,
  TranslationOverviewCatalogRow,
  TranslationOverviewDetailParams,
  TranslationOverviewListParams,
  TranslationOverviewListResponse,
  TranslationOverviewLocaleCatalogRow,
  TranslationOverviewLocaleRow,
  TranslationOverviewQueuedResponse,
  TranslationOverviewTranslatePayload,
  TranslationOverviewUploadPayload,
  TranslationOverviewUploadPendingPayload,
  TranslationOverviewUploadQueuedResponse,
  TranslationOverviewUploadResult,
} from '~/components/CustomerCommunication/TranslationOverview/types';

import { json_headers, request } from '~/services/common';

import { makeCustomerCommunicationV1URL } from './customerCommunicationRequestUtils';

type Wrapped<T> = { result: T };

function buildOverviewUrl(path: `/${string}`, params?: Record<string, string | number | undefined>): string {
  const url = makeCustomerCommunicationV1URL(`/translation-overview${path}`);

  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === '') {
      return;
    }
    url.searchParams.set(key, String(value));
  });

  return url.toString();
}

/** The API pages from 1; MUI DataGrid pages from 0. */
function toApiPage(page: number): number {
  return page + 1;
}

function toListParams(params: TranslationOverviewListParams) {
  return {
    page: toApiPage(params.page),
    pageSize: params.pageSize,
    search: params.search,
    status: params.status,
    uploadStatus: params.uploadStatus,
    sortBy: params.sortBy,
    sortDirection: params.sortDirection,
  };
}

function toDetailParams(params: TranslationOverviewDetailParams) {
  return {
    page: toApiPage(params.page),
    pageSize: params.pageSize,
    search: params.search,
    status: params.status,
  };
}

/** Counts the grids render directly; a row missing one must read as zero, never as `undefined`. */
const COUNT_FIELDS = [
  'readyCount',
  'pendingCount',
  'failedCount',
  'requiresTranslationCount',
  'uploadedCount',
  'pendingUploadCount',
  'uploadFailedCount',
] as const;

function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Backfills any count the API leaves out. The cells call `toLocaleString()` on these straight from
 * the row, so an older API - or a new counter the service has not shipped yet - would otherwise
 * crash the whole grid instead of showing a zero.
 */
function normaliseCounts<TRow>(row: TRow): TRow {
  const normalised: Record<string, unknown> = { ...(row as Record<string, unknown>) };

  COUNT_FIELDS.forEach((field) => {
    normalised[field] = toCount(normalised[field]);
  });

  return normalised as TRow;
}

async function getList<TRow>(
  url: string,
  signal?: AbortSignal,
  mapRow: (row: TRow) => TRow = (row) => row,
): Promise<TranslationOverviewListResponse<TRow>> {
  const response: Wrapped<TranslationOverviewListResponse<TRow>> = await request(url, {
    method: 'GET',
    credentials: 'include',
    headers: json_headers,
    signal,
  });

  const { rows, total } = response.result ?? { rows: [], total: 0 };

  return { rows: (rows ?? []).map(mapRow), total: toCount(total) };
}

/** POSTs whose payload is the response body itself, with no `result` envelope. */
async function postForBody<TBody>(url: string, body: unknown): Promise<TBody> {
  return request(url, {
    method: 'POST',
    credentials: 'include',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

/** POSTs that wrap their payload in the shared `result` envelope. */
async function postForResult<TResult>(url: string, body: unknown): Promise<TResult> {
  const response: Wrapped<TResult> = await postForBody(url, body);

  return response.result;
}

export function requestGetTranslationOverviewCatalogs(
  params: TranslationOverviewCatalogListParams,
  signal?: AbortSignal,
): Promise<TranslationOverviewListResponse<TranslationOverviewCatalogRow>> {
  return getList(
    buildOverviewUrl('/catalogs', { ...toListParams(params), brand: params.brand }),
    signal,
    normaliseCounts,
  );
}

export function requestGetTranslationOverviewLocales(
  params: TranslationOverviewListParams,
  signal?: AbortSignal,
): Promise<TranslationOverviewListResponse<TranslationOverviewLocaleRow>> {
  return getList(buildOverviewUrl('/locales', toListParams(params)), signal, normaliseCounts);
}

export function requestGetTranslationOverviewCatalogLocales(
  catalogId: string,
  params: TranslationOverviewDetailParams,
  signal?: AbortSignal,
): Promise<TranslationOverviewListResponse<TranslationOverviewCatalogLocaleRow>> {
  if (!catalogId) {
    throw new Error('missing catalog id');
  }

  return getList(
    buildOverviewUrl(`/catalogs/${encodeURIComponent(catalogId)}/locales`, toDetailParams(params)),
    signal,
  );
}

export function requestGetTranslationOverviewLocaleCatalogs(
  locale: string,
  params: TranslationOverviewDetailParams,
  signal?: AbortSignal,
): Promise<TranslationOverviewListResponse<TranslationOverviewLocaleCatalogRow>> {
  if (!locale) {
    throw new Error('missing locale');
  }

  return getList(buildOverviewUrl(`/locales/${encodeURIComponent(locale)}/catalogs`, toDetailParams(params)), signal);
}

export function requestTranslateTranslationOverview(
  payload: TranslationOverviewTranslatePayload,
): Promise<TranslationOverviewQueuedResponse> {
  return postForBody(buildOverviewUrl('/translate'), payload);
}

export function requestRefreshTranslationOverview(
  payload: TranslationOverviewTranslatePayload,
): Promise<TranslationOverviewQueuedResponse> {
  return postForBody(buildOverviewUrl('/refresh'), payload);
}

/**
 * Queues a Braze upload for every fully translated catalog holding rows that have not been
 * uploaded since they were translated. Only enqueues - the worker sends them one catalog at a
 * time - so this answers immediately however large the backlog is.
 */
export function requestUploadPendingTranslationOverview(
  payload: TranslationOverviewUploadPendingPayload,
): Promise<TranslationOverviewUploadQueuedResponse> {
  return postForBody(buildOverviewUrl('/upload-pending'), payload);
}

export function requestUploadTranslationOverviewLocale(
  payload: TranslationOverviewUploadPayload,
): Promise<Array<TranslationOverviewUploadResult>> {
  if (!payload.locale) {
    throw new Error('missing locale');
  }

  return postForResult(buildOverviewUrl('/upload'), payload);
}
